import React from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Label,
  Modal,
  Select,
  useNotification
} from '@sopranodesign/pulse-components';
import { useQueryClient } from '@sopranodesign/pulse-hooks';

import {
  deleteJiraProject,
  saveJiraProjects,
  resyncJiraProject,
  resyncAllJira,
  disconnectJira,
  recrawlJiraIssue
} from '../api';
import WarningMessageBody from './WarningMessageBody';

const JiraModalManager = ({
  activeModal,
  modalProps,
  closeModal,
  projects,
  setErrorIds,
  setProjectLoadingIds,
  setIssueLoadingIds,
  setIsSyncingAll
}) => {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [selectedProjects, setSelectedProjects] = React.useState([]);

  const handleSaveProjects = async () => {
    try {
      const payload = selectedProjects.map(project => ({
        id: project.value,
        key: project.key,
        name: project.label
      }));
      await saveJiraProjects(payload);
      queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/'] });
      showSuccess('Issues fetched successfully!');
      closeModal();
      setSelectedProjects([]);
    } catch (error) {
      showError(`Failed to fetch issues: ${error.message}`);
    }
  };

  const confirmationActions = {
    delete: {
      header: 'Confirm Delete Project',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete the project "${modalProps.project?.project_name}"?`}
          contentBody="All synced issues will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        const projectId = modalProps.project.id;
        setProjectLoadingIds(prev => ({ ...prev, [projectId]: true }));
        try {
          await deleteJiraProject(projectId);
          showSuccess(`Project "${modalProps.project.project_name}" delete requested`);
          queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to delete project: ${error.message}`);
        } finally {
          setProjectLoadingIds(prev => ({ ...prev, [projectId]: false }));
        }
      }
    },
    disconnect: {
      header: 'Confirm Disconnect Jira',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to disconnect Jira?"
          contentBody="All synced projects and issues will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Disconnect',
      handleConfirm: async () => {
        try {
          await disconnectJira();
          showSuccess('Jira disconnect requested');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to disconnect Jira: ${error.message}`);
        }
      }
    },
    resync: {
      header: 'Confirm Resync Project',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to resync the project "${modalProps.project?.project_name}"?`}
          contentBody="This will refresh its content."
        />
      ),
      confirmText: 'Resync',
      handleConfirm: async () => {
        const projectId = modalProps.project.id;
        setProjectLoadingIds(prev => ({ ...prev, [projectId]: true }));
        try {
          await resyncJiraProject(projectId);
          showSuccess('Project resync requested.');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/issues', projectId] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync project: ${error.message}`);
        } finally {
          setProjectLoadingIds(prev => ({ ...prev, [projectId]: false }));
        }
      }
    },
    resyncAll: {
      header: 'Confirm Resync All Projects',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to resync all projects?"
          contentBody="This will refresh all projects' content."
        />
      ),
      confirmText: 'Resync All',
      handleConfirm: async () => {
        setIsSyncingAll(true);
        try {
          await resyncAllJira();
          showSuccess('Projects resync requested.');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync projects: ${error.message}`);
        } finally {
          setIsSyncingAll(false);
        }
      }
    },
    recrawl: {
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl the issue "${modalProps.issue?.issue_key}"?`}
          contentBody="This will update its content."
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        const issueId = modalProps.issue.id;
        const projectId = modalProps.projectId;

        setErrorIds(prev => {
          const next = { ...prev };
          delete next[issueId];
          return next;
        });
        setIssueLoadingIds(prev => ({ ...prev, [issueId]: true }));

        try {
          await recrawlJiraIssue(issueId);
          showSuccess('Issue recrawl requested');
          queryClient.invalidateQueries({ queryKey: ['generative-ai/jira/issues', projectId] });
        } catch (error) {
          if (error.response?.status >= 500) {
            setErrorIds(prev => ({ ...prev, [issueId]: 'Failed to recrawl the issue.' }));
          }
          showError(`Failed to recrawl issue: ${error.message}`);
        } finally {
          setIssueLoadingIds(prev => ({ ...prev, [issueId]: false }));
          closeModal();
        }
      }
    }
  };

  if (!activeModal) {
    return null;
  }

  switch (activeModal) {
    case 'projects':
      return (
        <Modal
          header="Jira Projects"
          handleCancel={closeModal}
          handleConfirm={handleSaveProjects}
          confirmText="Select"
          cancelText="Cancel"
          disableConfirmButton={selectedProjects.length === 0}
          body={
            <Flex col gap="sm">
              <Label htmlFor="projectSelect">Project</Label>
              <Select
                id="projectSelect"
                value={selectedProjects}
                onChange={setSelectedProjects}
                options={projects.map(project => ({ label: project.name, value: project.id, key: project.key }))}
                placeholder="Select a project"
                isMulti
              />
            </Flex>
          }
        />
      );

    case 'confirmation': {
      const config = confirmationActions[modalProps.type];
      return (
        <Modal
          header={config.header}
          body={config.body}
          cancelText="Cancel"
          confirmText={config.confirmText}
          handleCancel={config.handleCancel ?? closeModal}
          handleConfirm={config.handleConfirm}
        />
      );
    }

    default:
      return null;
  }
};

JiraModalManager.propTypes = {
  activeModal: PropTypes.string,
  modalProps: PropTypes.object,
  closeModal: PropTypes.func.isRequired,
  projects: PropTypes.array,
  setErrorIds: PropTypes.func.isRequired,
  setProjectLoadingIds: PropTypes.func.isRequired,
  setIssueLoadingIds: PropTypes.func.isRequired,
  setIsSyncingAll: PropTypes.func.isRequired
};

export default JiraModalManager;
